import { useEffect, useRef, useState } from 'react';
import {
  draftAssetKeysOf,
  useEditAreaDraftStore,
  useEditAreaQueryCache,
  useFacePhotoUrls,
} from '@/entities/edit-area';
import { useInvalidateProducts, useProductSnapshot } from '@/entities/product';
import { runEditAreaSessionAction } from './edit-area-session-action';
import { useEditAreaMutations } from './use-edit-area-mutations';

export const useEditAreaSession = () => {
  const store = useEditAreaDraftStore();
  const productId = store.getState().draft.productId;
  const cache = useEditAreaQueryCache(productId);
  const fetchPhotoUrls = useFacePhotoUrls(productId);
  const fetchSnapshot = useProductSnapshot(productId);
  const invalidation = useInvalidateProducts();
  const mutations = useEditAreaMutations();
  const active = useRef(true);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    active.current = true;
    return () => {
      active.current = false;
    };
  }, []);

  const run = async (action: 'save' | 'undo' | 'redo'): Promise<void> => {
    setError(null);
    setMessage(null);
    try {
      const applied = await runEditAreaSessionAction(
        store,
        {
          ...cache,
          ...mutations,
          fetchSnapshot,
          afterSave: () => {
            invalidation.detail(productId);
            invalidation.list();
          },
          isActive: () => active.current,
        },
        action,
      );
      if (applied && active.current)
        setMessage(
          action === 'save'
            ? '편집영역을 새 버전으로 저장했습니다.'
            : '저장된 버전을 불러왔습니다. 고객의 최신 정의는 바뀌지 않습니다.',
        );
    } catch (cause) {
      // 서버가 실측 불일치·미등록으로 저장을 거부했을 수 있다 — 상품 상세를 다시 읽어
      // 워크스페이스가 새 실측을 Provider에 넘기고 불일치 안내로 잇게 한다. 입력은 건드리지 않는다.
      if (action === 'save') invalidation.detail(productId);
      if (active.current)
        setError(
          cause instanceof Error
            ? cause.message
            : '편집영역 요청에 실패했습니다. 다시 시도해 주세요.',
        );
    }
  };

  const checkServer = async (): Promise<void> => {
    if (store.getState().locked) return;
    const generation = store.getState().generation;
    store.getState().setLocked(true);
    setError(null);
    setMessage(null);
    try {
      // 색상 사진 키에 면 합성 에셋(마스크·음영·안내선) 키를 더해 표시 URL 을 함께 다시 받는다(Q11).
      const [response, refreshed] = await Promise.all([
        cache.fetchState(),
        fetchPhotoUrls(draftAssetKeysOf(store.getState())),
      ]);
      if (!active.current || generation !== store.getState().generation) return;
      // URL 갱신만 병합한다. 확인용 조회로 미저장 입력을 덮어쓰지 않는다.
      store.getState().reconcile(
        store.getState().draft.colors.map((color) => color.colorOptionId),
        response?.viewing.definition,
      );
      store.getState().mergePhotoUrls(refreshed.photos);
      setMessage(
        response
          ? `서버 조회 v${response.viewing.version} / 최신 v${response.latestVersion}. 현재 입력은 유지했습니다. 저장 재시도는 새 버전을 만들 수 있습니다.`
          : '서버에 저장된 정의가 없습니다. 현재 입력은 유지했습니다.',
      );
    } catch (cause) {
      if (active.current)
        setError(cause instanceof Error ? cause.message : '서버 상태 확인에 실패했습니다.');
    } finally {
      if (active.current && generation === store.getState().generation)
        store.getState().setLocked(false);
    }
  };
  return { run, checkServer, message, error };
};
